// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The module slots of a filing (component design §9.1–9.3): which module code went into
 * which slot, and what the server says about that module.
 *
 * Every code is looked up as it lands (`GET /archives/modules/lookup`), so a module that is
 * fitted elsewhere, of the wrong type, built to another BOM version or missing its own
 * lower modules is refused while the operator still has it in hand — and one that has no
 * archive yet is offered for on-the-spot filing. The server re-checks all of it at filing.
 *
 * A scan into the shared scan box goes to the first empty slot of the type the code names
 * (the letter in a module SN or archive number), so modules can be scanned in any order.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { ApiError } from "@/api/client";
import { api } from "@/api/endpoints";
import type { ModuleLookup } from "@/api/types";
import { type FitProblem, fitProblem, moduleCodeType } from "./moduleCodes";
import { normalizeSerial } from "./serials";

export interface ModuleSlot {
  /** `<assembly position code>#n`, as the server expands it. */
  positionCode: string;
  positionName: string;
  moduleType: string;
}

export type ModuleSlotState =
  | { status: "empty" }
  | { status: "checking" }
  | { status: "fitted"; module: ModuleLookup }
  | { status: "refused"; module: ModuleLookup; problem: FitProblem }
  | { status: "duplicate"; module: ModuleLookup }
  | { status: "not_found" }
  | { status: "failed"; error: unknown };

export type ModuleScanOutcome =
  | { kind: "module-filled"; positionCode: string }
  | { kind: "module-no-slot"; moduleType: string };

export interface ModuleSlotsState {
  values: Record<string, string>;
  states: Record<string, ModuleSlotState>;
  /** The first slot without a fitted module, or null. */
  nextSlot: string | null;
  filledCount: number;
  isComplete: boolean;
  /** Slot → the module SN to file it with; fitted slots only. */
  modulesBySlot: Record<string, string>;
  set: (positionCode: string, raw: string) => void;
  /** A code from the scan box; null when it is not a module code at all. */
  scan: (raw: string) => ModuleScanOutcome | null;
  clear: () => void;
  restore: (values: Record<string, string>) => void;
}

export function useModuleSlots(slots: ModuleSlot[], bomId: string): ModuleSlotsState {
  const [values, setValues] = useState<Record<string, string>>({});
  const [lookups, setLookups] = useState<Record<string, ModuleSlotState>>({});
  // A slot rescanned before the last lookup came back keeps the newer answer.
  const latest = useRef<Record<string, string>>({});

  const look = useCallback((positionCode: string, raw: string) => {
    const code = normalizeSerial(raw);
    latest.current[positionCode] = code;
    if (!code) {
      setLookups((previous) => ({ ...previous, [positionCode]: { status: "empty" } }));
      return;
    }
    setLookups((previous) => ({ ...previous, [positionCode]: { status: "checking" } }));
    api.archives.lookupModule(code).then(
      (module) => {
        if (latest.current[positionCode] !== code) return;
        setLookups((previous) => ({
          ...previous,
          [positionCode]: { status: "fitted", module } as ModuleSlotState,
        }));
      },
      (error: unknown) => {
        if (latest.current[positionCode] !== code) return;
        const notFound = error instanceof ApiError && error.code === "MODULE_NOT_FOUND";
        setLookups((previous) => ({
          ...previous,
          [positionCode]: notFound ? { status: "not_found" } : { status: "failed", error },
        }));
      },
    );
  }, []);

  const set = useCallback(
    (positionCode: string, raw: string) => {
      setValues((previous) => ({ ...previous, [positionCode]: raw }));
      look(positionCode, raw);
    },
    [look],
  );

  // The rules are applied here rather than when the answer arrives: they depend on the
  // slot and on the other slots, which may change after it.
  const states = useMemo(() => {
    const result: Record<string, ModuleSlotState> = {};
    const seen = new Map<string, number>();
    for (const slot of slots) {
      const state = lookups[slot.positionCode];
      if (state?.status === "fitted") {
        const id = state.module.archive_id;
        seen.set(id, (seen.get(id) ?? 0) + 1);
      }
    }
    for (const slot of slots) {
      const state = lookups[slot.positionCode] ?? { status: "empty" };
      if (!normalizeSerial(values[slot.positionCode])) {
        result[slot.positionCode] = { status: "empty" };
      } else if (state.status === "fitted") {
        const problem = fitProblem(state.module, { moduleType: slot.moduleType, bomId });
        result[slot.positionCode] = problem
          ? { status: "refused", module: state.module, problem }
          : (seen.get(state.module.archive_id) ?? 0) > 1
            ? { status: "duplicate", module: state.module }
            : state;
      } else {
        result[slot.positionCode] = state;
      }
    }
    return result;
  }, [slots, lookups, values, bomId]);

  const derived = useMemo(() => {
    const unfitted = slots.filter((slot) => states[slot.positionCode]?.status !== "fitted");
    const modulesBySlot: Record<string, string> = {};
    for (const slot of slots) {
      const state = states[slot.positionCode];
      if (state?.status === "fitted") modulesBySlot[slot.positionCode] = state.module.module_sn;
    }
    return {
      nextSlot: unfitted[0]?.positionCode ?? null,
      filledCount: slots.length - unfitted.length,
      isComplete: unfitted.length === 0,
      modulesBySlot,
    };
  }, [slots, states]);

  const scan = useCallback(
    (raw: string): ModuleScanOutcome | null => {
      const moduleType = moduleCodeType(raw);
      if (!moduleType || slots.length === 0) return null;
      const target = slots.find(
        (slot) =>
          slot.moduleType === moduleType && !normalizeSerial(values[slot.positionCode] ?? ""),
      );
      if (!target) return { kind: "module-no-slot", moduleType };
      set(target.positionCode, raw);
      return { kind: "module-filled", positionCode: target.positionCode };
    },
    [slots, values, set],
  );

  const clear = useCallback(() => {
    setValues({});
    setLookups({});
    latest.current = {};
  }, []);

  const restore = useCallback(
    (restored: Record<string, string>) => {
      setValues(restored);
      setLookups({});
      for (const [positionCode, raw] of Object.entries(restored)) look(positionCode, raw);
    },
    [look],
  );

  return { values, states, set, scan, clear, restore, ...derived };
}

/** The module slots a scope holds, one per fitted module, named after their BOM line. */
export function expandModuleSlots(
  moduleSlots: { module_type: string; position_code?: string; quantity: number }[],
  items: { position_code: string; material_info: string; display_name?: string }[],
): ModuleSlot[] {
  return moduleSlots.flatMap((slot) => {
    const code = slot.position_code ?? slot.module_type;
    const line = items.find((item) => item.position_code === code);
    const name = line ? line.display_name || line.material_info : code;
    return Array.from({ length: slot.quantity }, (_, index) => ({
      positionCode: `${code}#${index + 1}`,
      positionName: `${name}#${index + 1}`,
      moduleType: slot.module_type,
    }));
  });
}
